import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { memo, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import type { Share } from '@frameio/graphql-client/generated';

/**
 * This context is made for managing selected Shares for All Shares page in conjunction with SelectionContext.
 */

type SelectedShareContextValues = {
  selectedShare: Partial<Share> | null;
  setSelectedShare: Dispatch<SetStateAction<Partial<Share> | null>>;
};

export const SelectedShareContext = createContext<SelectedShareContextValues>({
  selectedShare: null,
  setSelectedShare: noop,
});

export const SelectedShareContextProvider = memo(
  ({ children }: PropsWithChildren<Record<never, any>>) => {
    const [selectedShare, setSelectedShare] = useState<Partial<Share> | null>(
      null
    );

    const context = useMemo(() => {
      return {
        selectedShare,
        setSelectedShare,
      };
    }, [selectedShare]);

    return (
      <SelectedShareContext.Provider value={context}>
        {children}
      </SelectedShareContext.Provider>
    );
  }
);

export const useGetSelectedShareContext = () => {
  const activeShare = useContextSelector(
    SelectedShareContext,
    (context) => context.selectedShare
  );

  return activeShare;
};

export const useSetSelectedShareContext = () => {
  const setActiveShare = useContextSelector(
    SelectedShareContext,
    (context) => context.setSelectedShare
  );

  return setActiveShare;
};
